import type { BoardData, Item, ItemKind, Meta, Mix } from './schema.js';

/**
 * Selectable symbol styles per item kind (decision U12), default first. Kinds without an entry
 * have a single built-in symbol. Mics: `standard` = circle with a tangential line on the pickup
 * side, `capsule` = the older capsule drawing, `polar` = the polar pattern itself (U39).
 */
export const SYMBOL_STYLES: Readonly<Partial<Record<ItemKind, readonly string[]>>> = {
  microphone: ['standard', 'capsule', 'polar'],
};

/**
 * Whether the polar pattern hint is drawn around mics (U39, default on); the `polar` symbol
 * style shows the pattern anyway.
 */
export function polarHintsOn(meta: Pick<Meta, 'polarHints'>): boolean {
  return meta.polarHints !== false;
}

/** Styles offered for a kind (empty when the kind has only one symbol). */
export function symbolStylesOf(kind: ItemKind): readonly string[] {
  return SYMBOL_STYLES[kind] ?? [];
}

/** Default style of a kind (undefined when the kind has only one symbol). */
export function defaultSymbolStyle(kind: ItemKind): string | undefined {
  return symbolStylesOf(kind)[0];
}

function known(kind: ItemKind, style: string | undefined): style is string {
  return style !== undefined && symbolStylesOf(kind).includes(style);
}

/** The board's style for a kind: `meta.symbolStyle[kind]` when known, else the default. */
export function boardSymbolStyle(
  meta: Pick<Meta, 'symbolStyle'>,
  kind: ItemKind,
): string | undefined {
  const s = meta.symbolStyle?.[kind];
  return known(kind, s) ? s : defaultSymbolStyle(kind);
}

/**
 * Style an item is drawn with: its own `symbol` override, else the board's style for its kind,
 * else the kind's default. Unknown names (e.g. written by a newer version) fall back.
 */
export function symbolStyleOf(
  meta: Pick<Meta, 'symbolStyle'>,
  item: Pick<Item, 'kind' | 'symbol'> &
    Partial<Pick<Item, 'label' | 'model' | 'props' | 'light' | 'truss' | 'ports'>>,
): string | undefined {
  if (item.kind === 'instrument') return instrumentShape(item);
  // lights are drawn per fixture type (OIL-14)
  if (item.kind === 'light') return item.light?.type;
  if (item.kind === 'truss') return item.truss?.shape;
  // stereo / USB DIs (U73)
  if (item.kind === 'di') return diShape(item);
  // chairs, stools, music stands (U72)
  if (item.kind === 'generic') return furnitureOf(item) ?? undefined;
  return known(item.kind, item.symbol) ? item.symbol : boardSymbolStyle(meta, item.kind);
}

/** DI symbol (U73): `usb` with a USB input, `stereo` with two or more mic outputs, else mono. */
export function diShape(item: Partial<Pick<Item, 'ports'>>): 'usb' | 'stereo' | undefined {
  const ports = item.ports ?? [];
  if (ports.some((p) => p.id === 'usb' && p.direction === 'in')) return 'usb';
  const outs = ports.filter((p) => p.direction === 'out' && p.signal === 'mic').length;
  return outs >= 2 ? 'stereo' : undefined;
}

/** Stage furniture (U72), stored as `props.furniture` of generic objects. */
export const FURNITURE_TYPES = [
  'chair',
  'chair_arms',
  'stool',
  'bar_stool',
  'music_stand',
] as const;
export type FurnitureType = (typeof FURNITURE_TYPES)[number];

/** The furniture type of a generic object, else null. */
export function furnitureOf(
  item: Pick<Item, 'kind'> & Partial<Pick<Item, 'props'>>,
): FurnitureType | null {
  const f = item.kind === 'generic' ? item.props?.['furniture'] : undefined;
  return (FURNITURE_TYPES as readonly unknown[]).includes(f) ? (f as FurnitureType) : null;
}

const GRAND_PIANO = /fl[üu]gel|\bgrand\b|\bgrand ?piano|steinway [a-e]\b|yamaha c[1-7]\b/iu;

/**
 * Silhouette of an instrument: `grand_piano` for grand pianos (library sub-type or label/model
 * "Flügel", "Grand Piano", "Steinway B" …), else undefined (the generic instrument symbol).
 */
export function instrumentShape(
  item: Partial<Pick<Item, 'label' | 'model' | 'props'>>,
): string | undefined {
  if (item.props?.subtype === 'grand_piano') return 'grand_piano';
  const text = `${item.label ?? ''} ${item.model ?? ''}`;
  return GRAND_PIANO.test(text) ? 'grand_piano' : undefined;
}

/** Whether wedges / monitors show the number of the mix (bus) feeding them (U40, default on). */
export function mixBadgesOn(meta: Pick<Meta, 'mixBadges'>): boolean {
  return meta.mixBadges !== false;
}

/** Mix numbers per fed item ("3", or "3,4" when several mixes feed it), for the canvas (U40). */
export function mixBadges(mixes: Readonly<Record<string, Mix>>): Map<string, string> {
  const by = new Map<string, number[]>();
  for (const m of Object.values(mixes))
    for (const id of new Set(m.outputItemIds)) by.set(id, [...(by.get(id) ?? []), m.number]);
  return new Map([...by].map(([id, ns]) => [id, ns.sort((a, b) => a - b).join(',')]));
}

/** Small signs of a wireless receiver / IEM on the plot (U75). */
export interface ReceiverBadges {
  /** Wireless (antenna) or wired (cable). */
  link: 'wireless' | 'wired';
  /** IEMs: mono / stereo from the mix feeding them; unknown without a mix. */
  audio?: 'mono' | 'stereo';
}

/**
 * Receiver signs (U75): IEM packs / transmitters and wireless mic receivers (an RF input). The
 * link is the item's RF link (receivers default to wireless); IEMs show mono / stereo after the
 * mix (bus) feeding them.
 */
export function receiverBadges(
  data: Pick<BoardData, 'mixes'>,
  item: Pick<Item, 'id' | 'kind'> & Partial<Pick<Item, 'rf' | 'ports'>>,
): ReceiverBadges | null {
  const rfIn = (item.ports ?? []).some((p) => p.direction === 'in' && p.signal === 'wireless');
  if (item.kind !== 'iem' && !(item.kind === 'generic' && rfIn)) return null;
  const badges: ReceiverBadges = { link: item.rf?.link ?? 'wireless' };
  if (item.kind === 'iem') {
    const mixes = Object.values(data.mixes).filter((m) => m.outputItemIds.includes(item.id));
    if (mixes.length > 0) badges.audio = mixes.some((m) => m.stereo) ? 'stereo' : 'mono';
  }
  return badges;
}
